import { c as _c } from "react/compiler-runtime";
import { PortableText } from "@portabletext/react";
import { AutoGiroAgreement, AvtaleGiroAgreement, Distribution, TaxUnit, VippsAgreement } from "../../../../../models";
import { GenericList } from "../GenericList";
import { ListRow } from "../GenericListRow";
import { AgreementDetails, AgreementDetailsConfiguration } from "./AgreementDetails";
import { useState } from "react";
import { StoppedAgreementFeedback } from "../../../agreements/StoppedAgreementFeedback/StoppedAgreementFeedback";
import { Lightbox } from "../../../../shared/components/Lightbox/Lightbox";
import { thousandize } from "../../../../../util/formatting";
export type AgreementTypes = "Vipps" | "AvtaleGiro" | "AutoGiro";
type AgreementRow = {
  ID: number;
  status: string | boolean;
  KID: string;
  date: number;
  amount: number;
  type: AgreementTypes;
  endpoint: string;
};
type AgreementListConfigurationColumn = {
  title: string;
  value: keyof AgreementRow;
  type: "string" | "sum" | "date" | "paymentmethod";
  width?: string;
  payment_date_format_template?: string;
  payment_date_last_day_of_month_template?: string;
  hide_on_mobile?: boolean;
};
type AgreementListConfiguration = {
  title: string;
  subtitle_text: string;
  list_empty_content: any[];
  columns: AgreementListConfigurationColumn[];
  details_configuration: AgreementDetailsConfiguration;
};
export const AgreementList: React.FC<{
  avtalegiro: AvtaleGiroAgreement[];
  vipps: VippsAgreement[];
  autogiro: AutoGiroAgreement[];
  distributions: Map<string, Distribution>;
  taxUnits: TaxUnit[];
  expandable?: boolean;
  configuration: AgreementListConfiguration;
}> = t0 => {
  const $ = _c(25);
  const {
    avtalegiro,
    vipps,
    autogiro,
    distributions,
    taxUnits,
    expandable,
    configuration
  } = t0;
  const [agreementCancelled, setAgreementCancelled] = useState(null);
  let headers;
  let t1;
  if ($[0] !== autogiro || $[1] !== avtalegiro || $[2] !== configuration.columns || $[3] !== configuration.details_configuration || $[4] !== distributions || $[5] !== taxUnits || $[6] !== vipps) {
    const columns = configuration.columns.filter(_temp);
    headers = columns.map(_temp2);
    const vippsType = vipps.map(_temp3);
    const giroType = avtalegiro.map(_temp4);
    const autoGiroType = autogiro.map(_temp5);
    const rowData = [...vippsType, ...giroType, ...autoGiroType];
    t1 = rowData.map(agreement => ({
      id: agreement.ID.toString(),
      defaultExpanded: false,
      cells: columns.map(column_1 => ({
        value: formatColumnValue(column_1, agreement[column_1.value])
      })),
      details: <AgreementDetails type={agreement.type} agreementKid={agreement.KID} endpoint={agreement.endpoint} agreementId={agreement.ID.toString()} inputDistribution={distributions.get(agreement.KID) as Distribution} taxUnits={taxUnits} inputSum={agreement.amount} inputDate={agreement.date} configuration={configuration.details_configuration} agreementCancelled={(agreementType, agreementId, agreementKid) => setAgreementCancelled({
        agreementType,
        agreementId,
        agreementKid
      })} />,
      element: agreement
    }));
    $[0] = autogiro;
    $[1] = avtalegiro;
    $[2] = configuration.columns;
    $[3] = configuration.details_configuration;
    $[4] = distributions;
    $[5] = taxUnits;
    $[6] = vipps;
    $[7] = headers;
    $[8] = t1;
  } else {
    headers = $[7];
    t1 = $[8];
  }
  const rows = t1;
  let t2;
  if ($[9] !== configuration.list_empty_content) {
    t2 = <div data-cy="agreement-list-empty-placeholder"><PortableText value={configuration.list_empty_content} /></div>;
    $[9] = configuration.list_empty_content;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const emptyPlaceholder = t2;
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [20, 70];
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== configuration.subtitle_text || $[13] !== configuration.title || $[14] !== emptyPlaceholder || $[15] !== expandable || $[16] !== headers || $[17] !== rows) {
    t4 = <GenericList emptyPlaceholder={emptyPlaceholder} title={configuration.title} supplementalInformation={configuration.subtitle_text} headers={headers} rows={rows} expandable={expandable} proportions={t3} />;
    $[12] = configuration.subtitle_text;
    $[13] = configuration.title;
    $[14] = emptyPlaceholder;
    $[15] = expandable;
    $[16] = headers;
    $[17] = rows;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== agreementCancelled || $[20] !== configuration.details_configuration) {
    t5 = configuration.details_configuration && configuration.details_configuration.agreement_cancelled_lightbox && <Lightbox open={!!agreementCancelled} onConfirm={() => setAgreementCancelled(null)} confirmLabel={configuration.details_configuration.agreement_cancelled_lightbox.lightbox_button_text}>{agreementCancelled && <StoppedAgreementFeedback title={configuration.details_configuration.agreement_cancelled_lightbox.title} text={configuration.details_configuration.agreement_cancelled_lightbox.text} agreementType={agreementCancelled.agreementType} agreementId={agreementCancelled.agreementId} KID={agreementCancelled.agreementKid} />}</Lightbox>;
    $[19] = agreementCancelled;
    $[20] = configuration.details_configuration;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== t4 || $[23] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
};
const formatColumnValue = (column: AgreementListConfigurationColumn, value: any) => {
  switch (column.type) {
    case "string":
      return value;
    case "sum":
      return thousandize(Math.round(parseFloat(value))) + " kr";
    case "date":
      return value === 0 ? column.payment_date_last_day_of_month_template : column.payment_date_format_template?.replaceAll("{{date}}", value);
    case "paymentmethod":
      return value;
  }
};
function _temp(column) {
  return window && !(window.innerWidth < 1180 && column.hide_on_mobile);
}
function _temp2(column_0) {
  return {
    label: column_0.title,
    width: column_0.width
  };
}
function _temp3(entry) {
  return {
    ID: entry.ID,
    status: entry.status,
    KID: entry.KID,
    date: entry.monthly_charge_day,
    amount: entry.amount,
    type: "Vipps",
    endpoint: entry.agreement_url_code
  };
}
function _temp4(entry_0) {
  return {
    ID: entry_0.ID,
    status: entry_0.active == 1,
    KID: entry_0.KID,
    date: entry_0.payment_date,
    amount: parseFloat(entry_0.amount),
    type: "AvtaleGiro",
    endpoint: entry_0.KID
  };
}
function _temp5(entry_1) {
  return {
    ID: entry_1.ID,
    status: entry_1.active,
    KID: entry_1.KID,
    date: entry_1.payment_date,
    amount: parseFloat(entry_1.amount),
    type: "AutoGiro",
    endpoint: entry_1.KID
  };
}